import { ProductSlugs } from '@deque/billing-utils';
import { apiRequest } from '../../../api-client';

export interface TeamsConnection {
  id: string;
  product_slug: ProductSlugs.axeAssistantTeams;
  enterprise_id: string;
  connected_user_id: string;
  status: 'connected' | 'disconnected' | 'deleted';
  created_at: string;
  updated_at: string;
  // The tenant ID for this Teams connection
  instance_id: string;
  // These are required for integration connections but not actually used for Teams
  credential_id: string;
  base_url: string;
  token_metadata: Record<string, unknown> | null;
}

interface RequestBody {
  enterpriseId: string;
  token: string;
}

interface RequestByIdBody extends RequestBody {
  connectionId: string;
}

export interface CreateTeamsConnectionBody {
  product_slug: ProductSlugs.axeAssistantTeams;
  instance_id: string;
  credential_id?: string;
  base_url?: string;
}

interface TeamsConnectionRequest {
  enterpriseId: string;
  token: string;
  path?: string;
  method?: string;
  body?: Record<string, unknown>;
}

// use the existing connection endpoint & avoide reworking on the CRUD operations for now
// TODO: Tech debt: refactor to use the new connection endpoint with new route
const teamsConnectionRequest = <T,>({
  enterpriseId,
  token,
  path = '',
  method,
  body
}: TeamsConnectionRequest): Promise<T> => {
  return apiRequest<T>({
    endpoint: `/api/integrations/enterprises/${enterpriseId}/connections${path}`,
    method,
    body,
    token
  });
};

/**
 * Get all Teams connections (tenant IDs) for the enterprise
 *
 * @param param0 Enterprise ID and auth token
 * @returns Array of Teams connections, each with a different tenant ID
 */
export const getTeamsConnections = ({ enterpriseId, token }: RequestBody) => {
  return teamsConnectionRequest<TeamsConnection[]>({
    enterpriseId,
    token
  }).then(connections =>
    connections.filter(
      // Not Good, need to move filter to backend
      connection => connection.product_slug === ProductSlugs.axeAssistantTeams
    )
  );
};

/**
 * Get a specific Teams connection by ID
 *
 * @param param0 Enterprise ID, token, and connection ID
 * @returns The Teams connection if it exists
 */
export const getTeamsConnectionById = ({
  enterpriseId,
  token,
  connectionId
}: RequestByIdBody) => {
  return teamsConnectionRequest<TeamsConnection>({
    enterpriseId,
    path: `/${connectionId}`,
    token
  });
};

/**
 * Get a Teams connection by tenant ID
 *
 * @param param0 Enterprise ID, token, and tenant ID to find
 * @returns The Teams connection with the specified tenant ID if it exists
 */
export const getTeamsConnectionByTenantId = async ({
  enterpriseId,
  token,
  tenantId
}: RequestBody & { tenantId: string }) => {
  const connections = await getTeamsConnections({ enterpriseId, token });
  return (
    connections.find(connection => connection.instance_id === tenantId) || null
  );
};

/**
 * Add a new tenant ID connection
 *
 * @param param0 Enterprise ID, token, and the tenant ID to add
 * @returns The created Teams connection
 */
export const addTenantId = ({
  enterpriseId,
  token,
  tenantId,
  userId
}: RequestBody & { tenantId: string; userId: string }) => {
  return teamsConnectionRequest<TeamsConnection>({
    enterpriseId,
    method: 'POST',
    body: {
      product_slug: ProductSlugs.axeAssistantTeams,
      instance_id: tenantId,
      credential_id: userId,
      base_url: 'none',
      status: 'connected'
    },
    token
  });
};

/**
 * Remove a tenant ID connection
 *
 * @param param0 Enterprise ID, token, and the tenant ID to remove
 * @returns The result of the delete operation
 */
export const removeTenantId = ({
  enterpriseId,
  token,
  tenantId
}: RequestBody & { tenantId: string }) => {
  // First find the connection with this tenant ID
  return getTeamsConnectionByTenantId({ enterpriseId, token, tenantId }).then(
    connection => {
      if (!connection) {
        return { success: false };
      }

      return teamsConnectionRequest<{ success: boolean }>({
        enterpriseId,
        path: `/${connection.id}`,
        method: 'DELETE',
        token
      });
    }
  );
};

/**
 * Remove a tenant ID connection **by its connection ID**
 *
 * @param param0 Enterprise ID, token, and the **connection ID** to remove
 * @returns The result of the delete operation
 */
export const deleteTenantIdById = ({
  enterpriseId,
  token,
  connectionId
}: RequestByIdBody) => {
  return teamsConnectionRequest<{ success: boolean }>({
    enterpriseId,
    path: `/${connectionId}`,
    method: 'DELETE',
    token
  });
};

/**
 * Update a Teams connection status
 *
 * @param param0 Enterprise ID, token, tenant ID and status to update to
 * @returns The updated Teams connection
 */
export const updateByStatus = ({
  enterpriseId,
  token,
  tenantId,
  status
}: RequestBody & {
  tenantId: string;
  status: 'connected' | 'disconnected';
}) => {
  // First find the connection with this tenant ID
  return getTeamsConnectionByTenantId({ enterpriseId, token, tenantId }).then(
    connection => {
      if (!connection) {
        return null;
      }

      return teamsConnectionRequest<TeamsConnection>({
        enterpriseId,
        path: `/${connection.id}`,
        method: 'PUT',
        body: {
          status,
          ...(status === 'connected' && {
            credential_id: connection.credential_id,
            base_url: connection.base_url
          })
        },
        token
      });
    }
  );
};

/**
 * Update a specific Teams connection, particularly its tenant ID (instance_id)
 *
 * @param param0 Enterprise ID, token, connection ID, the new tenant ID value, and user ID
 * @returns The updated Teams connection
 */
export const updateTenantIdById = ({
  enterpriseId,
  token,
  connectionId,
  tenantId,
  userId
}: RequestByIdBody & { tenantId: string; userId: string }) => {
  return teamsConnectionRequest<TeamsConnection>({
    enterpriseId,
    path: `/${connectionId}`,
    method: 'PUT',
    body: {
      instance_id: tenantId,
      credential_id: userId, // Assuming this maps to credential_id
      base_url: 'none', // Assuming this is needed
      status: 'connected' // Assuming we keep it connected
    },
    token
  });
};
